import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCheck, FiX, FiAlertTriangle } from 'react-icons/fi';

const ComparisonSection = () => {
  const comparisonData = [
    {
      category: "Primary Focus",
      wtb: "Root Cause (Cognitive Skills)",
      tutoring: "Academic Content",
      accommodations: "Environmental Modifications",
      apps: "Narrow Skill Gamification"
    },
    {
      category: "Methodology",
      wtb: "Personalized Cognitive Therapy",
      tutoring: "Re-teaching Curriculum",
      accommodations: "Extended Time, Preferenced Seating",
      apps: "One-size-fits-all puzzles"
    },
    {
      category: "Core Outcome",
      wtb: "Self-Sustaining Learning Skills",
      tutoring: "Short-Term Grade Improvement",
      accommodations: "Access to Content",
      apps: "Improved Game Score"
    },
    {
      category: "Impact on Anxiety",
      wtb: "check",
      tutoring: "warning",
      accommodations: "warning",
      apps: "x"
    },
    {
      category: "Therapist",
      wtb: "MS, CCC-SLP (EF Specialist)",
      tutoring: "Varies",
      accommodations: "Teacher",
      apps: "Algorithm"
    },
    {
      category: "Investment",
      wtb: "$135-200/session",
      tutoring: "$50-100/hour ongoing",
      accommodations: "Free",
      apps: "Often free, limited gains"
    },
    {
      category: "Long-term Result",
      wtb: "check",
      tutoring: "x",
      accommodations: "x",
      apps: "x"
    }
  ];

  const getIcon = (value) => {
    switch (value) {
      case "check":
        return <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-600" />;
      case "x":
        return <SafeIcon icon={FiX} className="h-5 w-5 text-red-600" />;
      case "warning":
        return <SafeIcon icon={FiAlertTriangle} className="h-5 w-5 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getStatusText = (value) => {
    switch (value) {
      case "check":
        return "✅ Significant Reduction";
      case "x":
        return "❌ Poor Transfer to Academics";
      case "warning":
        return "⚠️ Temporary Relief";
      default:
        return value;
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            The We The Billions Difference: Building Skills vs. Teaching Content
          </h2>
        </motion.div>

        {/* Desktop Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="hidden lg:block overflow-x-auto"
        >
          <table className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
            <thead>
              <tr className="bg-gradient-to-r from-accent-magenta to-accent-purple text-white">
                <th className="px-6 py-4 text-left font-bold"></th>
                <th className="px-6 py-4 text-left font-bold">We The Billions</th>
                <th className="px-6 py-4 text-left font-bold">Traditional Tutoring</th>
                <th className="px-6 py-4 text-left font-bold">School Accommodations</th>
                <th className="px-6 py-4 text-left font-bold">Generic Brain Training Apps</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row, index) => (
                <motion.tr
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-6 py-4 font-semibold text-gray-900 bg-gray-50">{row.category}</td>
                  <td className="px-6 py-4 text-gray-900 font-medium bg-accent-light/20">
                    {getIcon(row.wtb) || row.wtb}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {getIcon(row.tutoring) || row.tutoring}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {getIcon(row.accommodations) || row.accommodations}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {getIcon(row.apps) || row.apps}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-8">
          {comparisonData.map((row, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">{row.category}</h3>
              <div className="space-y-3">
                <div className="p-4 bg-accent-light/20 rounded-lg border-l-4 border-accent-magenta">
                  <div className="font-semibold text-accent-magenta mb-1">We The Billions</div>
                  <div className="flex items-center gap-2">
                    {getIcon(row.wtb)}
                    <span>{getStatusText(row.wtb)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-gray-900 mb-1 text-sm">Traditional Tutoring</div>
                    <div className="text-sm text-gray-600 flex items-center gap-1">
                      {getIcon(row.tutoring)}
                      <span>{getStatusText(row.tutoring)}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-gray-900 mb-1 text-sm">School Accommodations</div>
                    <div className="text-sm text-gray-600 flex items-center gap-1">
                      {getIcon(row.accommodations)}
                      <span>{getStatusText(row.accommodations)}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-gray-900 mb-1 text-sm">Brain Training Apps</div>
                    <div className="text-sm text-gray-600 flex items-center gap-1">
                      {getIcon(row.apps)}
                      <span>{getStatusText(row.apps)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Special callouts */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <div className="p-6 bg-green-50 rounded-xl border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-600" />
              <span className="font-bold text-green-800">Significant Reduction</span>
            </div>
            <p className="text-sm text-green-700">in anxiety and stress</p>
          </div>
          <div className="p-6 bg-yellow-50 rounded-xl border border-yellow-200">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiAlertTriangle} className="h-6 w-6 text-yellow-600" />
              <span className="font-bold text-yellow-800">Often Increases</span>
            </div>
            <p className="text-sm text-yellow-700">pressure and anxiety</p>
          </div>
          <div className="p-6 bg-yellow-50 rounded-xl border border-yellow-200">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiAlertTriangle} className="h-6 w-6 text-yellow-600" />
              <span className="font-bold text-yellow-800">Temporary Relief</span>
            </div>
            <p className="text-sm text-yellow-700">doesn't address root cause</p>
          </div>
          <div className="p-6 bg-red-50 rounded-xl border border-red-200">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiX} className="h-6 w-6 text-red-600" />
              <span className="font-bold text-red-800">Unaddressed</span>
            </div>
            <p className="text-sm text-red-700">anxiety remains problematic</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonSection;